/**
 * ReportPanel - 报告面板组合组件
 * 组装 StepTabs、FileSelector、Toolbar、Editor 等子组件
 * 业务逻辑通过 useReportPanel hook 管理
 */
import React, { useRef } from 'react';
import { useSize } from 'ahooks';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { ReportStepTabs } from './ReportStepTabs';
import { ReportFileSelector } from './ReportFileSelector';
import { ReportToolbar } from './ReportToolbar';
import { ReportEditor } from './ReportEditor';
import { ReportModal } from './ReportModal';
import { useReportPanel } from './useReportPanel';
import { IReportPanelProps } from './types';
// 引入原有的 MarkdownEditor 组件
import { MarkdownEditor } from '../../ChatDSTemplate/components';
import './ReportPanel.scss';

export const ReportPanel: React.FC<IReportPanelProps> = ({
  sessionId,
  conversations,
  dsReport,
  streamingState,
  handleUpdateDsReport,
  startNewConversation,
  showCloseButton = true,
  className,
  onHeaderMouseDown,
  onFocus,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth = 0 } = useSize(containerRef) || {};
  const { formatMessage } = useIntl();

  // 使用业务逻辑 hook
  const {
    stepActive,
    fileActive,
    htmlReport,
    currentButton,
    reportFileList,
    loading,
    showCopyTips,
    showModal,
    modalContent,
    isDisabledReportTab,
    markdownRef,
    reportLogoRef,
    reportRef,
    handleStepClick,
    handleFileItemClick,
    handleAction,
    handleExport,
    handleCloseModal,
  } = useReportPanel({
    sessionId,
    conversations,
    dsReport,
    streamingState,
    handleUpdateDsReport,
    startNewConversation,
  });

  return (
    <div
      ref={containerRef}
      className={classNames('dba-report-panel', className)}
      // 整个面板区域触发层级切换
      onMouseDown={onFocus}
      onClick={onFocus}
    >
      {/* 头部区域 - 整个头部支持拖拽 */}
      <div
        className={classNames('dba-report-panel__header', {
          'dba-report-panel__header--draggable': !!onHeaderMouseDown,
        })}
        onMouseDown={(e) => {
          // 先触发层级切换
          onFocus?.();
          // 再触发拖拽
          onHeaderMouseDown?.(e);
        }}
      >
        {/* 步骤页签 - 可点击元素需要阻止拖拽冒泡 */}
        <ReportStepTabs
          activeStep={stepActive}
          isReportDisabled={isDisabledReportTab}
          onStepChange={(step) => handleStepClick(step)}
          onClose={() => handleUpdateDsReport({ show: false })}
          showCloseButton={showCloseButton}
        />

        {/* 工具栏区域 - 不阻止冒泡，让内部元素各自处理 */}
        <div className="dba-report-panel__toolbar">
          {/* 文件选择器 */}
          <div className="dba-report-panel__toolbar-left">
            <ReportFileSelector
              fileList={reportFileList}
              currentFile={currentButton}
              activeFileKey={fileActive}
              containerWidth={containerWidth}
              activeStep={stepActive}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onFileSelect={handleFileItemClick}
            />
          </div>

          {/* 操作按钮 */}
          <div className="dba-report-panel__toolbar-right">
            <ReportToolbar
              activeStep={stepActive}
              streamingState={streamingState}
              showCopyToast={showCopyTips}
              onActionClick={handleAction}
              onExport={handleExport}
            />
          </div>
        </div>
      </div>

      {/* 报告编辑区 */}
      <ReportEditor
        content={htmlReport}
        answer={dsReport?.answer}
        isLoading={loading}
        ref={markdownRef}
        logoContainerRef={reportLogoRef}
        reportContainerRef={reportRef}
        MarkdownEditorComponent={MarkdownEditor}
      />

      {/* 确认弹窗 */}
      <ReportModal
        showModal={showModal}
        modalContent={modalContent}
        formatMessage={formatMessage}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default ReportPanel;
